import {KTCard, toAbsoluteUrl} from '_metronic/helpers'
import StatusBadge from 'app/components/StatusBadge'
import {useAuth} from 'app/modules/auth'
import React, {useEffect, useMemo, useState} from 'react'
import db from 'services/firebase'
import {collection, doc, onSnapshot, updateDoc} from 'firebase/firestore'
import {Link, useNavigate, useParams} from 'react-router-dom'
import ReviewFilterBar from '../components/Filters/ReviewFilterBar'
import EmptyState from 'app/components/EmptyState'
import {ToastContainer} from 'react-toastify'
import ViewSystem from '../components/Drawer/ViewSystem'
import OwnerDisplay from 'app/components/OwnerDisplay'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import {ReviewUser} from '../utils/PagesUtils'
import ExportReviewToExcel from '../components/ExportReviewToExcel'
import StatusItem from '../components/StatusIcon'

// Define interfaces for type safety
interface Owner {
  id: string
  name: string
}

interface Member {
  id: string
  login: string
  name?: string
  mfaEnabled?: boolean
  platform: string
  orgLogin?: string
  orgName?: string
  status: string
  createdAt?: any
  updatedAt?: any
  tenantId: string
  uid: string
  owner?: Owner
  role?: string
  reviewStatus?: string
  reviewDecision?: 'Approved' | 'Denied'
}

interface Reviewer {
  id: string
  name: string
  email: string
}

interface System {
  systemId: string
  name: string
  domain?: string
  icon?: string
  integrationStatus: string
  reviewer?: Reviewer
  status?: string
  users?: {[key: string]: ReviewUser}
}

interface Review {
  id: string
  reviewName: string
  status: string
  systems: {[key: string]: System}
}

interface SystemItem {
  id: string
  system: System
  reviewName?: string
  status?: string
  createdAt?: any
  dueDate?: any
}

const SystemDetails = () => {
  const [sortBy, setSortBy] = useState<string>('accountName')
  const [sortDirection, setSortDirection] = useState<string>('asc')
  const [members, setMembers] = useState<Member[]>([])
  const {currentUser} = useAuth()
  const [reviews, setReviews] = useState<Review[]>([])
  const {id, systemId} = useParams<{id: string; systemId: string}>()
  const [owners, setOwners] = useState<Owner[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectReviewer, setSelectReviewer] = useState<string>('')
  const [selectStatus, setSelectStatus] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [viewSystemDrawer, setViewSystemDrawer] = useState<boolean>(false)
  const [accountId, setAccountId] = useState<string>('')
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const selectedSystem = reviews
    .flatMap((review) =>
      Object.entries(review.systems || {}).map(([sysId, system]) => ({
        ...review,
        system: {...system, systemId: sysId},
      }))
    )
    .find((item) => item.system.systemId === systemId)

  const systemItemsForExport: SystemItem[] = useMemo(() => {
    if (!selectedSystem) return []
    return [selectedSystem as SystemItem]
  }, [selectedSystem])

  useEffect(() => {
    getReviews()
  }, [id])

  const getReviews = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId || !id) {
        setIsLoading(false)
        return
      }
      const tenantRef = doc(db, 'tenants', tenantId)
      const policyRef = collection(tenantRef, 'reviews')

      const unsubscribe = onSnapshot(doc(policyRef, id), (snapshot) => {
        const policyData = snapshot.data()
        setReviews(policyData ? [{id: snapshot.id, ...policyData} as Review] : [])
        setIsLoading(false)
      })

      return () => unsubscribe()
    } catch (error) {
      console.error('Error getting reviews:', error)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!selectedSystem || !currentUser?.tenantId) return

    const tenantId = currentUser.tenantId
    const systemName = selectedSystem.system.name.toLowerCase()
    const accessReviewRef = collection(db, 'tenants', tenantId, 'access_review')

    const unsubscribe = onSnapshot(
      accessReviewRef,
      (snapshot) => {
        const allMembers: Member[] = []
        snapshot.docs.forEach((doc) => {
          const assetData = doc.data()
          if (assetData.platform?.toLowerCase() !== systemName) return

          const membersData = assetData.members || {}
          const org = assetData.org || {}

          Object.keys(membersData).forEach((memberKey) => {
            const member = membersData[memberKey]
            allMembers.push({
              id: `${doc.id}-${memberKey}`,
              login: member.login,
              name: member.name,
              mfaEnabled: member.mfaEnabled,
              platform: assetData.platform,
              orgLogin: org.login,
              orgName: org.name,
              status: member.status,
              createdAt: member.createdAt,
              updatedAt: member.updatedAt,
              tenantId: assetData.tenantId,
              uid: assetData.uid,
              owner: member.owner,
              role: member.accountRole,
            })
          })
        })
        setMembers(allMembers)
        setIsLoading(false)
      },
      (error) => {
        console.error('Error fetching members:', error)
        setIsLoading(false)
      }
    )

    return () => unsubscribe()
  }, [selectedSystem, currentUser])

  const filteredAndSortedMembers = useMemo(() => {
    let filtered = [...members]

    if (searchTerm) {
      filtered = filtered.filter(
        (member) =>
          member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          member.login?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          member.owner?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (selectReviewer) {
      filtered = filtered.filter((member) => member.owner?.id === selectReviewer)
    }

    if (selectStatus) {
      filtered = filtered.filter((member) => getUserStatus(member.id) === selectStatus)
    }

    return filtered.sort((a, b) => {
      if (sortBy === 'accountName') {
        const valueA = a.name || a.login || ''
        const valueB = b.name || b.login || ''
        return sortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA)
      } else if (sortBy === 'accountOwner') {
        const valueA = a.owner?.name || ''
        const valueB = b.owner?.name || ''
        return sortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA)
      } else if (sortBy === 'role') {
        const valueA = a.role || ''
        const valueB = b.role || ''
        return sortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA)
      } else if (sortBy === 'reviewStatus') {
        const valueA = getUserStatus(a.id) || ''
        const valueB = getUserStatus(b.id) || ''
        return sortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA)
      } else if (sortBy === 'reviewDecision') {
        const valueA =
          getUserStatus(a.id) === 'Approved' || getUserStatus(a.id) === 'Denied'
            ? 'Completed'
            : 'Pending'
        const valueB =
          getUserStatus(b.id) === 'Approved' || getUserStatus(b.id) === 'Denied'
            ? 'Completed'
            : 'Pending'
        return sortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA)
      }
      return 0
    })
  }, [members, searchTerm, selectReviewer, selectStatus, sortBy, sortDirection])

  const handleSort = (column: string) => {
    const newSortDirection = column === sortBy && sortDirection === 'asc' ? 'desc' : 'asc'
    setSortBy(column)
    setSortDirection(newSortDirection)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const getUserStatus = (memberId: string): string => {
    return reviews[0]?.systems?.[systemId as string]?.users?.[memberId]?.status || 'Pending'
  }

  const ownerAssignmentStatus = useMemo(() => {
    const totalMembers = filteredAndSortedMembers.length
    const membersWithOwners = filteredAndSortedMembers.filter((member) => member.owner?.name).length
    return {completed: membersWithOwners, total: totalMembers}
  }, [filteredAndSortedMembers])

  const accessReviewStatus = useMemo(() => {
    const totalMembers = filteredAndSortedMembers.length
    const completedAccessReviews = filteredAndSortedMembers.filter((member) => {
      const status = getUserStatus(member.id)
      return status === 'Approved' || status === 'Denied'
    }).length
    return {completed: completedAccessReviews, total: totalMembers}
  }, [filteredAndSortedMembers, reviews, systemId])

  const noteAssignmentStatus = useMemo(() => {
    if (filteredAndSortedMembers.some((member) => getUserStatus(member.id) === 'Pending')) {
      return {completed: 0, total: filteredAndSortedMembers.length}
    }
    const deniedMembers = filteredAndSortedMembers.filter((member) => {
      const status = getUserStatus(member.id)
      const approvedAt = reviews[0]?.systems?.[systemId as string]?.users?.[member.id]?.approvedAt
      return status === 'Denied' && approvedAt != null
    }).length
    const completedNotes = filteredAndSortedMembers.filter((member) => {
      const status = getUserStatus(member.id)
      const approvedAt = reviews[0]?.systems?.[systemId as string]?.users?.[member.id]?.approvedAt
      if (status !== 'Denied' || approvedAt == null) return false
      const note = reviews[0]?.systems?.[systemId as string]?.users?.[member.id]?.note
      return note && note.trim() !== ''
    }).length
    return {completed: completedNotes, total: deniedMembers}
  }, [filteredAndSortedMembers, reviews, systemId])

  const tasksRemaining = useMemo(() => {
    let completedTasks = 0
    if (ownerAssignmentStatus.completed === ownerAssignmentStatus.total) completedTasks += 1
    if (accessReviewStatus.completed === accessReviewStatus.total) completedTasks += 1
    if (noteAssignmentStatus.completed === noteAssignmentStatus.total) completedTasks += 1
    return 3 - completedTasks
  }, [ownerAssignmentStatus, accessReviewStatus, noteAssignmentStatus])

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId || !id || !systemId) {
        throw new Error('Missing required parameters')
      }

      const reviewRef = doc(db, 'tenants', tenantId, 'reviews', id)
      const updatedData = reviews[0]

      if (updatedData?.systems?.[systemId]) {
        const systemUsers: {[key: string]: ReviewUser} = updatedData.systems[systemId].users || {}
        const systemUserEntries: ReviewUser[] = Object.values(systemUsers)
        const expectedUserCount = filteredAndSortedMembers.length

        if (expectedUserCount > 0 && systemUserEntries.length === expectedUserCount) {
          const validUsers = systemUserEntries.filter((user): user is ReviewUser =>
            Boolean(user && typeof user === 'object' && 'status' in user)
          )

          const allUsersProcessed = validUsers.every(
            (user) => (user.status === 'Approved' || user.status === 'Denied') && user.approvedAt
          )

          if (allUsersProcessed && validUsers.length === expectedUserCount) {
            await updateDoc(reviewRef, {
              [`systems.${systemId}.status`]: 'Completed',
              [`systems.${systemId}.approvedAt`]: new Date().toISOString(),
            })
            showSuccessToast({message: 'System status updated successfully'})
            navigate(`/access/review/${reviews[0]?.id}`, {
              state: {toast: {message: 'System status updated successfully', type: 'success'}},
            })
          } else {
            showErrorToast({message: 'Not all users have been reviewed.'})
          }
        } else {
          showErrorToast({message: 'User count mismatch or no users found.'})
        }
      } else {
        showErrorToast({message: 'System data not found.'})
      }
    } catch (error) {
      showErrorToast({message: 'Failed to update system status.'})
    } finally {
      setIsSubmitting(false)
    }
  }

  const isSystemCompleted = useMemo(() => {
    const completed = reviews[0]?.systems?.[systemId as string]?.status === 'Completed'
    return completed
  }, [reviews, systemId])

  return (
    <>
      <KTCard className='mb-5 mb-xl-8'>
        {selectedSystem && (
          <div className='px-9 py-6' key={selectedSystem.system.systemId}>
            <div className='d-flex justify-content-between align-items-center'>
              <div className='d-flex align-items-center gap-5'>
                {selectedSystem.system.icon && (
                  <img
                    src={toAbsoluteUrl(selectedSystem.system.icon)}
                    alt={`${selectedSystem.system.name} logo`}
                    style={{width: '35px', height: '35px'}}
                  />
                )}
                <div>
                  <div>
                    <Link
                      to='/access/review'
                      className='text-gray-600 fs-7 text-hover-dark'
                      style={{cursor: 'pointer'}}
                    >
                      Access Reviews
                    </Link>{' '}
                    /{' '}
                    <Link
                      to={`/access/review/${id}`}
                      className='text-gray-900 fw-semibold fs-7 text-hover-dark'
                      style={{cursor: 'pointer'}}
                    >
                      {selectedSystem.system.name.charAt(0).toUpperCase() +
                        selectedSystem.system.name.slice(1)}
                    </Link>
                  </div>
                  <div className='d-flex align-items-center gap-2 mt-2 mb-1'>
                    <h1 className='mb-0 fw-bold fs-3'>
                      {selectedSystem.system.name.charAt(0).toUpperCase() +
                        selectedSystem.system.name.slice(1)}
                    </h1>
                    <StatusBadge
                      status={
                        selectedSystem.system.integrationStatus as
                          | 'Active'
                          | 'Inactive'
                          | 'Deactivated'
                          | 'Pending'
                          | 'Due'
                          | 'Completed'
                          | 'Not Started'
                          | 'In review'
                          | 'Connected'
                      }
                    />
                  </div>
                  <span className='text-gray-700 fs-7'>{selectedSystem.system.domain}</span>
                </div>
              </div>
              <div className='d-flex align-items-center gap-3'>
                <ExportReviewToExcel
                  filteredItems={systemItemsForExport}
                  reviewName={reviews[0]?.reviewName}
                />
                {isSystemCompleted ? null : isSubmitting ? (
                  <button
                    className='btn btn-sm fs-6'
                    style={{background: '#0E9F6E', height: '40px', color: '#fff'}}
                    disabled
                  >
                    Submitting...{' '}
                    <span className='spinner-border spinner-border-sm align-middle'></span>
                  </button>
                ) : (
                  <button
                    className='btn btn-sm fs-6'
                    style={{background: '#0E9F6E', height: '40px', color: '#fff'}}
                    onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                      (e.currentTarget.style.backgroundColor = '#148363')
                    }
                    onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                      (e.currentTarget.style.backgroundColor = '#0E9F6E')
                    }
                    disabled={tasksRemaining > 0}
                    onClick={handleSubmit}
                  >
                    Submit
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </KTCard>

      <div
        className='card'
        style={{backgroundColor: '#F3FAF7', border: '1.5px solid #0E9F6E', marginBottom: '24px'}}
      >
        <div className='px-6 py-4'>
          <div className='align-items-between justify-content-between'>
            <div className='justify-content-start flex-column gap-1'>
              <span className='card-label fw-semibold fs-4'>Conduct your user access review</span>
              <p className='fw-normal fs-6' style={{color: '#5A5A5A'}}>
                You have {tasksRemaining} of 3 tasks remaining. Let’s get those completed to
                complete the access review.
              </p>
              <div className='mt-4'>
                <ul style={{listStyle: 'none', padding: 0}}>
                  <StatusItem
                    status={ownerAssignmentStatus}
                    label='Assign an owner to each account'
                  />
                  <StatusItem
                    status={accessReviewStatus}
                    label='For each account, indicate whether to maintain current access, remove access, or change access level'
                  />
                  <StatusItem
                    status={noteAssignmentStatus}
                    label='For accounts whose level of access is denied, add a note or upload  evidence'
                  />
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <KTCard>
        <ReviewFilterBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          owners={owners}
          selectedPlatform={selectReviewer}
          setSelectedPlatform={setSelectReviewer}
          selectedStatus={selectStatus}
          setSelectedStatus={setSelectStatus}
          handleInputChange={handleInputChange}
          type='reviewDetail'
        />
        <div className='card-body py-3'>
          <div className='table-responsive' style={{minHeight: '50vh', overflowY: 'auto'}}>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bold' style={{color: '#5A5A5A'}}>
                  <th className='min-w-200px' onClick={() => handleSort('accountName')}>
                    ACCOUNT NAME{' '}
                    {sortBy === 'accountName' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-200px' onClick={() => handleSort('accountOwner')}>
                    ACCOUNT OWNER{' '}
                    {sortBy === 'accountOwner' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-200px' onClick={() => handleSort('role')}>
                    ROLE{' '}
                    {sortBy === 'role' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-200px'>REVIEW STATUS </th>
                  <th className='min-w-200px'>REVIEW DECISION </th>
                  <th className='min-w-50px'></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr style={{height: '100%'}}>
                    <td colSpan={6}>
                      <EmptyState
                        title='Loading Members'
                        description='Please wait while we fetch the member details'
                        isLoading={true}
                      />
                    </td>
                  </tr>
                ) : filteredAndSortedMembers.length > 0 ? (
                  filteredAndSortedMembers.map((member) => (
                    <tr
                      key={member.id}
                      style={{
                        transition: 'background-color 0.2s ease',
                        cursor: 'pointer',
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.backgroundColor = 'oklch(98.5% 0.002 247.839)')
                      }
                      onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      onClick={() => {
                        setViewSystemDrawer(true)
                        setAccountId(member.id)
                      }}
                    >
                      <td>
                        <span className='fw-semibold d-block fs-6 text-dark'>
                          {member.name || '--'}
                        </span>
                      </td>
                      <td>
                        <span className='text-dark fw-semibold d-block fs-6'>
                          {member.owner ? <OwnerDisplay owner={member.owner.name} /> : '--'}
                        </span>
                      </td>
                      <td>
                        <span className='text-dark fw-semibold d-block fs-6'>
                          {member.role || '--'}
                        </span>
                      </td>
                      <td>
                        <span className='fw-semibold fs-6'>
                          <StatusBadge
                            status={
                              getUserStatus(member.id) as
                                | 'Active'
                                | 'Inactive'
                                | 'Deactivated'
                                | 'Pending'
                                | 'Due'
                                | 'Completed'
                                | 'Not Started'
                                | 'In review'
                                | 'Approved'
                                | 'Denied'
                            }
                          />
                        </span>
                      </td>
                      <td>
                        {getUserStatus(member.id) === 'Approved' ||
                        getUserStatus(member.id) === 'Denied' ? (
                          <span
                            className='ki-duotone ki-check-circle text-success fs-2'
                            style={{marginLeft: 20}}
                          >
                            <span className='path1'></span>
                            <span className='path2'></span>
                          </span>
                        ) : (
                          <span
                            className='ki-duotone ki-cross-circle fs-2'
                            style={{marginLeft: 20}}
                          >
                            <span className='path1'></span>
                            <span className='path2'></span>
                          </span>
                        )}
                      </td>
                      <td>
                        <button className='w-10 h-10 flex items-center border border-none justify-center bg-gray-100 rounded'>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='20'
                            height='20'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='icon icon-tabler icon-tabler-chevron-right'
                          >
                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                            <path d='M9 6l6 6l-6 6' />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr style={{height: '100%'}}>
                    <td colSpan={6}>
                      <EmptyState
                        title='No Members Found'
                        description='No members found for this system.'
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </KTCard>
      <ViewSystem
        isOpen={viewSystemDrawer}
        onClose={() => setViewSystemDrawer(false)}
        id={id as string}
        systemId={systemId as string}
        accountId={accountId}
        systemName={selectedSystem?.system?.name || ''}
        systemStatus={isSystemCompleted}
      />
      <ToastContainer />
    </>
  )
}

export default SystemDetails
